/**
 * Single source of truth for resolving duplicate decisions when multiple
 * decisions exist for the same csv_index (e.g. multiple same-name matches).
 * Priority: add_to_existing > create_new > skip.
 * Must stay in sync with backend resolution in candidates_controller.php bulkImport.
 *
 * @param {Array<{ csv_index: number, action: string, existing_id?: number }>} decisions
 * @returns {Array<{ csv_index: number, action: string, existing_id?: number }>} one effective decision per csv_index
 */
export function resolveEffectiveDecisions(decisions) {
  const list = Array.isArray(decisions) ? decisions : [];
  const map = {};
  for (const d of list) {
    if (d == null || typeof d !== 'object') {
      continue;
    }
    const idx = d.csv_index;
    if (idx === undefined || idx === null) {
      continue;
    }
    const current = map[idx];
    if (
      !current ||
      d.action === 'add_to_existing' ||
      (d.action === 'create_new' && current.action !== 'add_to_existing')
    ) {
      map[idx] = d;
    }
  }
  return Object.values(map);
}

/**
 * Same as resolveEffectiveDecisions but returns a map keyed by csv_index.
 * @param {Array<{ csv_index: number, action: string, existing_id?: number }>} decisions
 * @returns {Record<number, { csv_index: number, action: string, existing_id?: number }>}
 */
export function resolveEffectiveDecisionsByIndex(decisions) {
  const arr = resolveEffectiveDecisions(decisions);
  if (!Array.isArray(arr)) return {};
  return Object.fromEntries(arr.map(d => [d.csv_index, d]));
}

/**
 * Set of csv_index values (stringified) with a HIGH severity validation issue — these rows
 * are always dropped server-side regardless of duplicate decisions. Stringified because
 * csv_index in validation_issues comes from a plain PHP array index (a number), while
 * csv_index in exact_duplicates/possible_duplicates is read back from a SQL query and may
 * come across as a numeric string — a Set with strict equality would otherwise miss the match.
 *
 * @param {Array} validationIssues
 * @returns {Set<string>}
 */
export function getBlockedCsvIndices(validationIssues) {
  return new Set(
    (validationIssues ?? [])
      .filter(issue => issue?.severity === 'high')
      .map(issue => String(issue.csv_index)),
  );
}

/**
 * Compute how many records will actually be imported given a preview result and decisions.
 * Shared between BulkImport and RenewalsImport so the footer button count stays in sync
 * with what DuplicatesReview reports.
 *
 * @param {object|null} previewResult
 * @param {Array} decisions
 * @returns {number}
 */
export function getImportCount(previewResult, decisions) {
  if (!previewResult) return 0;
  const newCount = Number(previewResult.new_count) || 0;
  const blockedIndices = getBlockedCsvIndices(previewResult.validation_issues);
  const effective = resolveEffectiveDecisions(decisions ?? []);
  const fromDecisions = (effective ?? []).filter(
    d =>
      (d?.action === 'create_new' || d?.action === 'add_to_existing') &&
      !blockedIndices.has(String(d.csv_index)),
  );
  const createNewRows = new Set(
    fromDecisions.filter(d => d?.action === 'create_new').map(d => d.csv_index),
  ).size;
  const addToExistingCandidates = new Set(
    fromDecisions
      .filter(d => d?.action === 'add_to_existing' && d.existing_id != null)
      .map(d => d.existing_id),
  ).size;
  return newCount + createNewRows + addToExistingCandidates;
}
